// src/views/admin/ADAnneesScolaires.jsx
//
// PERMISSIONS (module "parametres") :
// - parametres.gerer_annees      : créer, modifier, activer/désactiver, archiver, restaurer
// - parametres.supprimer_annees  : supprimer une année (action distincte : irréversible)
// Le bouton "Statistiques" reste visible pour tous.

import { useState, useEffect } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";
import { usePermissions } from "../../context/PermissionsContext";

function Modal({ title, onClose, children }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div className="modal-title">
          {title}
          <button className="ibtn" style={{ border: "none" }} onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const EMPTY = { nom: "", date_debut: "", date_fin: "" };

export default function ADAnneesScolaires({ slug }) {
  const { canSee } = usePermissions();
  const peutGererAnnees = canSee("parametres.gerer_annees");
  const peutSupprimerAnnees = canSee("parametres.supprimer_annees");

  const [annees, setAnnees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilt, setStatusFilt] = useState("tous");
  const [modal, setModal] = useState(null); // create | edit | archive | delete | stats
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [stats, setStats] = useState(null);

  const toast = useToastHook();
  const {
    getAnneesScolaires,
    createAnneeScolaire,
    updateAnneeScolaire,
    deleteAnneeScolaire,
    setActiveAnneeScolaire,
    archiveAnneeScolaire,
    restoreAnneeScolaire,
    getAnneeScolaireStats,
  } = useAdminService();

  useEffect(() => {
    if (slug) loadData();
  }, [slug]);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await getAnneesScolaires(slug, { show_archived: "true" });
      setAnnees(data);
    } catch (error) {
      toast.error("Impossible de charger les années scolaires");
    } finally {
      setLoading(false);
    }
  };

  const filtered = annees.filter(a => {
    const matchSearch = a.nom?.toLowerCase().includes(search.toLowerCase());
    const matchStatus =
      statusFilt === "tous" ||
      (statusFilt === "actives" ? a.actif :
       statusFilt === "archived" ? a.archived : !a.actif && !a.archived);
    return matchSearch && matchStatus;
  });

  const openCreate = () => { setForm(EMPTY); setModal("create"); };
  const openEdit = (a) => {
    if (a.archived) {
      toast.info("Cette année est archivée et ne peut pas être modifiée");
      return;
    }
    setSelected(a);
    setForm({
      nom: a.nom,
      date_debut: a.date_debut || "",
      date_fin: a.date_fin || ""
    });
    setModal("edit");
  };
  const openArchive = (a) => {
    if (a.actif) {
      toast.error("Impossible d'archiver une année active");
      return;
    }
    setSelected(a);
    setModal("archive");
  };
  const openDelete = async (a) => {
    if (a.actif) {
      toast.error("Impossible de supprimer une année active");
      return;
    }
    setSelected(a);
    // Vérifier si l'année peut être supprimée
    try {
      const statsData = await getAnneeScolaireStats(slug, a.id);
      if (!statsData.peut_supprimer) {
        // Proposer l'archivage
        setStats(statsData);
        setModal("confirm-archive");
        return;
      }
      setModal("delete");
    } catch (error) {
      toast.error("Impossible de vérifier les données de l'année");
    }
  };

  const openStats = async (a) => {
    setSelected(a);
    setModal("stats");
    setStats(null);
    try {
      const data = await getAnneeScolaireStats(slug, a.id);
      setStats(data);
    } catch (error) {
      toast.error("Impossible de charger les statistiques");
      setModal(null);
    }
  };

  const handleFormChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!peutGererAnnees) return;
    if (!form.nom.trim()) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    try {
      const dataToSend = {
        nom: form.nom.trim(),
        date_debut: form.date_debut || null,
        date_fin: form.date_fin || null,
      };
      if (modal === "create") {
        const newAnnee = await createAnneeScolaire(slug, dataToSend);
        setAnnees(prev => [...prev, newAnnee]);
        toast.success(`Année scolaire "${newAnnee.nom}" créée avec succès`);
      } else {
        const updated = await updateAnneeScolaire(slug, selected.id, dataToSend);
        setAnnees(prev => prev.map(a => a.id === selected.id ? updated : a));
        toast.success(`Année scolaire "${updated.nom}" modifiée avec succès`);
      }
      setModal(null);
    } catch (error) {
      const data = error.response?.data;
      const msg = data?.nom?.[0] || data?.date_fin || data?.error || "Une erreur est survenue";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (a) => {
    if (!peutGererAnnees) return;
    try {
      const res = await setActiveAnneeScolaire(slug, a.id);
      setAnnees(prev => prev.map(x =>
        x.id === a.id ? res.annee : (res.annee.actif ? { ...x, actif: false } : x)
      ));
      toast.success(res.message);
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible de changer le statut");
    }
  };

  const handleArchive = async () => {
    if (!peutGererAnnees) return;
    setSaving(true);
    try {
      const res = await archiveAnneeScolaire(slug, selected.id);
      setAnnees(prev => prev.map(a => a.id === selected.id ? res.annee : a));
      toast.success(res.message);
      setModal(null);
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible d'archiver cette année");
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (a) => {
    if (!peutGererAnnees) return;
    try {
      const res = await restoreAnneeScolaire(slug, a.id);
      setAnnees(prev => prev.map(x => x.id === a.id ? res.annee : x));
      toast.success(res.message);
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible de restaurer cette année");
    }
  };

  const handleDelete = async () => {
    if (!peutSupprimerAnnees) return;
    setSaving(true);
    try {
      await deleteAnneeScolaire(slug, selected.id);
      setAnnees(prev => prev.filter(a => a.id !== selected.id));
      toast.success(`Année scolaire supprimée avec succès`);
      setModal(null);
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible de supprimer cette année scolaire");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmArchive = async () => {
    if (!peutGererAnnees) return;
    // L'utilisateur a choisi d'archiver au lieu de supprimer.
    // On garde l'année déjà sélectionnée si l'API ne renvoie pas `annee`.
    setModal(null);
    if (stats?.annee) setSelected(stats.annee);
    setTimeout(() => {
      setModal("archive");
    }, 100);
  };

  // Formatage des dates
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("fr-FR");
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR") + ' ' + date.toLocaleTimeString("fr-FR", { hour: '2-digit', minute: '2-digit' });
  };

  // Récupérer les infos d'archivage
  const getArchiveInfo = (annee) => {
    if (!annee.archived || !annee.date_archivage) return null;
    return {
      date: formatDateTime(annee.date_archivage),
      by: annee.archived_by_name || annee.archived_by_username || "Utilisateur inconnu"
    };
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="sb2">
        <input
          type="text"
          placeholder="Rechercher une année…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select value={statusFilt} onChange={e => setStatusFilt(e.target.value)}>
          <option value="tous">Tous</option>
          <option value="actives">Actives</option>
          <option value="archived">Archivées</option>
          <option value="normales">Normales</option>
        </select>
        <button className="bsec" onClick={loadData}><i className="ti ti-refresh" />Actualiser</button>
        {/* parametres.gerer_annees */}
        {peutGererAnnees && (
          <button className="bprim" onClick={openCreate}><i className="ti ti-plus" />Nouvelle année</button>
        )}
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Année</th>
              <th>Période</th>
              <th>Statut</th>
              <th style={{ textAlign: "center" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a, index) => {
              const archiveInfo = getArchiveInfo(a);
              return (
                <tr key={a.id} style={{ opacity: a.archived ? 0.8 : 1 }}>
                  <td style={{
                    fontWeight: 600,
                    color: a.archived ? "var(--m)" : "var(--pr)"
                  }}>
                    {a.nom}
                  </td>
                  <td style={{ fontSize: 13, color: "var(--m)" }}>
                    {formatDate(a.date_debut)} → {formatDate(a.date_fin)}
                  </td>
                  <td>
                    {a.actif && <span className="pill ok">Active</span>}
                    {a.archived && <span className="pill warn">Archivée</span>}
                    {!a.actif && !a.archived && <span className="pill neu">Inactive</span>}
                    {archiveInfo && (
                      <div style={{
                        fontSize: 11,
                        color: "var(--h)",
                        marginTop: 4,
                        lineHeight: 1.4
                      }}>
                        <div>Archivée le: {archiveInfo.date}</div>
                        <div>Par: {archiveInfo.by}</div>
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="act" style={{ justifyContent: "center" }}>
                      {/* Statistiques : visible pour tous */}
                      <button
                        title="Statistiques"
                        onClick={() => openStats(a)}
                        style={{ background: "#17a2b8", color: "white" }}
                        onMouseEnter={e => e.currentTarget.style.opacity = "0.8"}
                        onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                      >
                        <i className="ti ti-chart-bar" />
                      </button>

                      {/* Activer/Désactiver — parametres.gerer_annees */}
                      {peutGererAnnees && !a.archived && (
                        <button
                          title={a.actif ? "Désactiver" : "Activer"}
                          onClick={() => handleToggleActive(a)}
                          style={{
                            background: a.actif ? "#6c757d" : "#28a745",
                            color: "white"
                          }}
                          onMouseEnter={e => e.currentTarget.style.opacity = "0.8"}
                          onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                        >
                          <i className={a.actif ? "ti ti-toggle-right" : "ti ti-toggle-left"} />
                        </button>
                      )}

                      {/* Modifier — parametres.gerer_annees */}
                      {peutGererAnnees && !a.archived && (
                        <button
                          title="Modifier"
                          onClick={() => openEdit(a)}
                          style={{ background: "#ffc107", color: "black" }}
                          onMouseEnter={e => e.currentTarget.style.opacity = "0.8"}
                          onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                        >
                          <i className="ti ti-edit" />
                        </button>
                      )}

                      {/* Archiver — parametres.gerer_annees */}
                      {peutGererAnnees && !a.archived && !a.actif && (
                        <button
                          title="Archiver"
                          onClick={() => openArchive(a)}
                          style={{ background: "var(--pr)", color: "white" }}
                          onMouseEnter={e => e.currentTarget.style.opacity = "0.8"}
                          onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                        >
                          <i className="ti ti-archive" />
                        </button>
                      )}

                      {/* Restaurer — parametres.gerer_annees */}
                      {peutGererAnnees && a.archived && (
                        <button
                          title="Restaurer"
                          onClick={() => handleRestore(a)}
                          style={{ background: "#20c997", color: "white" }}
                          onMouseEnter={e => e.currentTarget.style.opacity = "0.8"}
                          onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                        >
                          <i className="ti ti-restore" />
                        </button>
                      )}

                      {/* Supprimer — parametres.supprimer_annees */}
                      {peutSupprimerAnnees && !a.archived && !a.actif && (
                        <button
                          className="del"
                          title="Supprimer"
                          onClick={() => openDelete(a)}
                        >
                          <i className="ti ti-trash" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan="4" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>
                  <i className="ti ti-calendar" style={{ fontSize: 32, display: "block", marginBottom: 8 }} />
                  {statusFilt === "archived"
                    ? "Aucune année archivée"
                    : "Aucune année scolaire trouvée"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Créer/Modifier */}
      {peutGererAnnees && (modal === "create" || modal === "edit") && (
        <Modal title={modal === "create" ? "Nouvelle année scolaire" : "Modifier l'année scolaire"} onClose={() => setModal(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <div className="fg">
              <label>Nom *</label>
              <input
                type="text"
                placeholder="Ex: 2024-2025"
                value={form.nom}
                onChange={e => handleFormChange('nom', e.target.value)}
              />
            </div>
            <div className="fgrid">
              <div className="fg">
                <label>Date de début</label>
                <input
                  type="date"
                  value={form.date_debut || ""}
                  onChange={e => handleFormChange('date_debut', e.target.value)}
                />
              </div>
              <div className="fg">
                <label>Date de fin</label>
                <input
                  type="date"
                  value={form.date_fin || ""}
                  onChange={e => handleFormChange('date_fin', e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSave} disabled={saving}>
              {saving ? (
                <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> En cours...</>
              ) : (
                <><i className="ti ti-check" /> {modal === "create" ? "Créer" : "Enregistrer"}</>
              )}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal Archive */}
      {peutGererAnnees && modal === "archive" && selected && (
        <Modal title="Archiver l'année scolaire" onClose={() => setModal(null)}>
          <div style={{ fontSize: 14, color: "var(--m)", lineHeight: 1.6 }}>
            <i className="ti ti-archive" style={{ fontSize: 20, marginRight: 8 }} />
            Archiver l'année scolaire <strong style={{ color: "var(--t)" }}>{selected.nom}</strong> ?
            Elle restera consultable mais ne pourra plus être modifiée.
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleArchive} disabled={saving}>
              {saving ? "Archivage..." : <><i className="ti ti-archive" /> Archiver</>}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal Delete */}
      {peutSupprimerAnnees && modal === "delete" && selected && (
        <Modal title="Confirmer la suppression" onClose={() => setModal(null)}>
          <div style={{ fontSize: 14, color: "var(--m)", lineHeight: 1.6 }}>
            <i className="ti ti-trash" style={{ fontSize: 20, marginRight: 8, color: "var(--rd)" }} />
            Supprimer l'année scolaire <strong style={{ color: "var(--t)" }}>{selected.nom}</strong> ?
            <br /><br />
            Cette année est vide et sera supprimée définitivement.
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim rd" onClick={handleDelete} disabled={saving}>
              {saving ? "Suppression..." : <><i className="ti ti-trash" />Supprimer</>}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal Confirm Archive (quand l'année contient des données) */}
      {peutSupprimerAnnees && modal === "confirm-archive" && stats && (
        <Modal title="Année non vide" onClose={() => setModal(null)}>
          <div style={{ fontSize: 14, lineHeight: 1.6 }}>
            <p style={{ color: "var(--m)" }}>
              Cette année scolaire contient des données et ne peut pas être supprimée :
            </p>
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 8,
              margin: "12px 0",
              padding: 12,
              background: "var(--s2)",
              borderRadius: 6
            }}>
              {Object.entries(stats.statistiques || {})
                .filter(([_, value]) => value > 0)
                .map(([key, value]) => (
                  <div key={key} style={{ fontSize: 13 }}>
                    <strong>{key}:</strong> {value}
                  </div>
                ))}
            </div>
            {peutGererAnnees && (
              <p style={{ color: "var(--m)" }}>
                Souhaitez-vous l'archiver à la place ?
              </p>
            )}
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)}>{peutGererAnnees ? "Annuler" : "Fermer"}</button>
            {peutGererAnnees && (
              <button className="bprim" onClick={handleConfirmArchive}>
                <i className="ti ti-archive" /> Archiver
              </button>
            )}
          </div>
        </Modal>
      )}

      {/* Modal Statistiques */}
      {modal === "stats" && selected && (
        <Modal title={`Statistiques — ${selected.nom}`} onClose={() => setModal(null)}>
          {!stats ? <Loader /> : (
            <div>
              <div style={{
                padding: 10,
                background: stats.peut_supprimer ? "var(--gn-bg)" : "var(--rd-bg)",
                borderRadius: 6,
                marginBottom: 16,
                color: stats.peut_supprimer ? "var(--gn)" : "var(--rd)",
                fontSize: 14
              }}>
                <strong>État :</strong> {stats.message_suppression || "Année vide"}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {Object.entries(stats.statistiques || {}).map(([key, val]) => (
                  <div key={key} className="card" style={{ padding: 12 }}>
                    <div style={{ fontSize: 12, color: "var(--h)", textTransform: "capitalize" }}>
                      {key.replace(/_/g, ' ')}
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 600, color: "var(--t)" }}>{val}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .act button {
          transition: opacity 0.2s;
        }
        .act button:hover {
          opacity: 0.8 !important;
        }
      `}</style>
    </div>
  );
}